import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { villaAPI } from '../config/api';
import { FiUsers, FiHome, FiArrowRight, FiPercent, FiMapPin, FiDroplet, FiHeart } from 'react-icons/fi';
import SEO from '../components/SEO';
import PictureImage from '../components/PictureImage';
import { useSettings } from '../context/SettingsContext';
import { withBhk } from '../utils/villaName';

const Villas = () => {
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    villaAPI.getAll()
      .then(res => setVillas(res.data))
      .catch(err => console.error('Error fetching villas:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="villas-page" className="pt-28">
      <SEO
        title="Lonavala Private Pool Villas — 3BHK to 12BHK Villas with Swimming Pool | Royal Garden Villas"
        description="Lonavala villa with pool for every group: 3BHK, 4BHK, 5BHK, 7BHK, 8BHK, 9BHK and 12BHK private pool villas for 6 to 35 guests in one gated estate. Fully serviced, 24/7 security, from ₹10,999/night. Book direct."
        path="/villas"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Villas', url: '/villas' },
        ]}
      />
      {/* Page Header */}
      <section className="py-16 md:py-20 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Our Collection</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Luxury <span className="text-[#F57C20] italic">Villas</span> in Lonavala
          </h1>
          <p className="text-white/60 mt-4 max-w-2xl text-sm md:text-base">
            Discover our curated collection of premium private pool villas in the lush hills of Lonavala.
            From intimate 3BHK getaways to the grand 12BHK Celebration Villa.
          </p>
        </div>
      </section>

      {/* Discount Strip — hidden when master toggle is off */}
      {nightDiscountEnabled && (
        <div className="bg-[#F57C20]/10 border-b border-[#F57C20]/20 py-3">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-center gap-3">
            <FiPercent className="w-4 h-4 text-[#F57C20]" />
            <span className="text-[#F57C20] text-sm font-semibold">Stay Longer, Pay Less: {nightDiscountPct}% OFF 2nd Night Onwards across all villas!</span>
          </div>
        </div>
      )}

      {/* Villa Grid */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1,2,3,4].map(i => <div key={i} className="shimmer h-96 rounded-xl" />)}
            </div>
          ) : villas.length === 0 ? (
            <div data-testid="no-villas" className="text-center py-20 text-[#6B7280]">No villas available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {villas.map((villa) => (
                <Link
                  key={villa.id}
                  to={`/villas/${villa.id}`}
                  data-testid={`villa-listing-${villa.id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <PictureImage
                      src={villa.images?.[0]}
                      alt={`${villa.name} - ${villa.bedrooms}BHK luxury private pool villa in Lonavala`}
                      width={800} height={500}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      {villa.is_combined && (
                        <span className="bg-[#1B2432] text-white text-[10px] font-bold tracking-wider px-3 py-1.5 rounded">All 3 Villas Combined</span>
                      )}
                    </div>
                    {nightDiscountEnabled && (
                      <div className="absolute top-4 right-4 bg-[#F57C20] text-white text-[10px] font-bold tracking-wider px-3 py-1.5 rounded">
                        {nightDiscountPct}% OFF 2nd Night+
                      </div>
                    )}
                    <button className="absolute bottom-4 right-4 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors" onClick={e => e.preventDefault()}>
                      <FiHeart className="w-4 h-4 text-[#1A1A2E]/60" />
                    </button>
                  </div>

                  <div className="p-6 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-[#1A1A2E] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {withBhk(villa)}
                    </h3>
                    <p className="text-[#6B7280] text-xs mb-3 flex items-center gap-1">
                      <FiMapPin className="w-3 h-3" /> Lonavala, Maharashtra
                    </p>

                    <div className="flex items-center gap-4 mb-3">
                      <span className="flex items-center gap-1.5 text-[#6B7280] text-xs"><FiHome className="w-3.5 h-3.5" /> {villa.bedrooms} BHK</span>
                      <span className="flex items-center gap-1.5 text-[#6B7280] text-xs"><FiUsers className="w-3.5 h-3.5" /> {villa.base_capacity} Guests</span>
                      <span className="flex items-center gap-1.5 text-[#6B7280] text-xs"><FiDroplet className="w-3.5 h-3.5" /> Pool</span>
                    </div>

                    <p className="text-[#9CA3AF] text-[10px] mb-3">For {villa.base_capacity} guests (addtl. guests +Rs.1,000/person/night)</p>

                    <p className="text-[#6B7280] text-sm line-clamp-2 mb-4">{villa.description}</p>

                    {/* Amenities */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {villa.amenities?.slice(0, 4).map((amenity, i) => (
                        <span key={i} className="text-xs text-[#6B7280] border border-gray-200 px-2.5 py-1 rounded">{amenity}</span>
                      ))}
                      {villa.amenities?.length > 4 && <span className="text-xs text-[#F57C20] px-2.5 py-1">+{villa.amenities.length - 4} more</span>}
                    </div>

                    {/* 15% off 2nd-night+ info */}
                    {nightDiscountEnabled && <div className="bg-[#F57C20]/5 border border-[#F57C20]/20 px-3 py-2 mb-2 rounded">
                      <p className="text-[#F57C20] text-xs font-semibold">
                        2 nights from Rs.{(villa.weekend_price + Math.round(villa.weekend_price * (1 - nightDiscountPct / 100))).toLocaleString()} (Night 2 &amp; beyond at {nightDiscountPct}% off)
                      </p>
                    </div>}

                    {/* Housekeeping hours disclosure */}
                    <div data-testid="villa-card-housekeeping" className="flex items-center gap-1.5 text-[11px] text-[#6B7280] mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-[#F57C20]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                      <span><strong>Housekeeping:</strong> 10 AM – 6 PM only</span>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <p className="text-[#1A1A2E] text-lg font-bold">Rs.{villa.weekend_price?.toLocaleString()}<span className="text-[#6B7280] text-sm font-normal"> / night</span></p>
                        <p className="text-[10px] text-[#9CA3AF]">For {villa.base_capacity} guests | Weekend price</p>
                      </div>
                      <span className="flex items-center gap-2 text-[#F57C20] text-sm font-semibold group-hover:gap-3 transition-all">
                        View Details <FiArrowRight />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-[#F7F8FA] rounded-xl p-8 md:p-12 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              {[
                { title: "Smart Booking", desc: "Booking the 12BHK Celebration Villa reserves all 3 individual villas for your exclusive use." },
                { title: "Extra Guests", desc: "Additional guests above base capacity at Rs.1,000/person/night. Floor mattresses provided." },
                ...(nightDiscountEnabled ? [{ title: "Stay Longer, Pay Less", desc: `1st night at actual rate. Every night from the 2nd onwards gets a flat ${nightDiscountPct}% off.` }] : []),
                { title: "Dynamic Pricing", desc: "Weekday & weekend rates. Seasonal offers and long-stay discounts available." },
              ].map((item, i) => (
                <div key={i}>
                  <p className="text-[#F57C20] text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</p>
                  <p className="text-[#6B7280] text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl text-center mb-6 text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Luxury Private Pool Villas in <span className="text-[#F57C20] italic">Lonavala</span>
          </h2>
          <div className="text-[#6B7280] text-sm leading-relaxed space-y-4">
            <p>
              Royal Garden Villas offers the finest collection of luxury private pool villas in Lonavala, Maharashtra.
              Our 7 luxury private pool villas are perfect for families, friend groups, corporate teams, and event organizers.
            </p>
            <p>
              Each villa comes equipped with a private swimming pool, landscaped gardens, fully-equipped kitchen,
              AC, complimentary high-speed WiFi, indoor games, parking, and 24/7 security.
            </p>
            <p>
              Our transparent pricing includes weekday and weekend rates{nightDiscountEnabled ? `, with ${nightDiscountPct}% off on every night from the 2nd onwards` : ""}.
              All bookings are 100% online with secure payment via Razorpay.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Villas;
